import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentLoader from "../ContentLoader";
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Tabs,
  Textarea,
} from "../ui";
import { confirmAlert, showAlert } from "../AppAlert/appAlert";
import ReportChartPreview from "./ReportChartPreview";
import {
  applyReportEdit,
  fetchReportEditOptions,
  fetchReportEditPayload,
  fetchReportSourceHtml,
  renderReportEditPdf,
  stageReportEditHtml,
  type ReportEditChartOption,
  type ReportEditLayout,
  type ReportEditOperation,
  type ReportEditOptionsResponse,
  type ReportEditPage,
  type ReportEditSlot,
} from "../../services/reportEditApi";
import { addOrUpdateReport } from "../../services/smeApi";

const CHART_SPACE_PADDING = 14;
const TEXT_MAX = 5000;

const ACCEPT_TEXT = ".md,.txt,.pdf,.docx";
const ACCEPT_IMAGE = ".png,.jpg,.jpeg";

export type ReportCustomizeTarget = {
  reportItemId: string;
  remarks?: string;
  sourceHtmlUrl?: string;
  status?: string;
};

type RegeneratedDraftPack = {
  pdfFile: File;
  sourceHtmlPath: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  projectId: number;
  clientUserId: number | string | null | undefined;
  report: ReportCustomizeTarget;
  onDraftSaved?: () => void;
};

const extractSavedReportItemId = (result: unknown): string | null => {
  const payload = (result as { data?: unknown })?.data ?? result;
  const record = payload as { items?: unknown[]; Items?: unknown[] };
  const items = record?.items ?? record?.Items;
  if (!Array.isArray(items) || items.length === 0) return null;
  const first = items[0] as { reportItemId?: string; ReportItemId?: string };
  return first?.reportItemId ?? first?.ReportItemId ?? null;
};

const slotRowLabel = (slot: ReportEditSlot) => {
  const page = slot.printed?.trim() || String(slot.page);
  const heading = slot.page_heading?.trim() || "Untitled page";
  return `Page ${page} — ${heading}`;
};

const pageLabel = (page: ReportEditPage) => {
  const num = page.printed?.trim() || String(page.page);
  const heading = page.heading?.trim();
  return heading ? `${num} — ${heading}` : `Page ${num}`;
};

const layoutNeedsChart = (layout: string) =>
  ["chart", "text_chart", "chart_image", "text_chart_image"].includes(layout);

const layoutNeedsText = (layout: string) =>
  ["text", "text_chart", "text_image", "text_chart_image"].includes(layout);

const layoutNeedsImage = (layout: string) =>
  ["text_image", "chart_image", "text_chart_image"].includes(layout);

const LAYOUT_DISPLAY_LABELS: Record<string, string> = {
  chart: "Chart only",
  text: "Content only",
  text_chart: "Content + chart",
  text_image: "Content + image",
  chart_image: "Chart + image",
  text_chart_image: "Content + chart + image",
};

const layoutDisplayLabel = (layout: ReportEditLayout) =>
  LAYOUT_DISPLAY_LABELS[layout.layout] || layout.label || layout.layout;

const formatChartOption = (chart: ReportEditChartOption, includeHeight: boolean) => {
  const name = chart.label?.trim() || chart.chart_id;
  return includeHeight ? `${name} (${chart.height}px)` : name;
};

const chartOptionSortKey = (chart: ReportEditChartOption) =>
  (chart.label?.trim() || chart.chart_id).toLowerCase();

const buildChartSelectOptions = (
  charts: ReportEditChartOption[],
  includeHeight: boolean
) =>
  [...charts]
    .sort((a, b) => chartOptionSortKey(a).localeCompare(chartOptionSortKey(b)))
    .map((c) => ({
      value: c.chart_id,
      label: formatChartOption(c, includeHeight),
    }));

type CustomizeFilePickerProps = {
  id: string;
  label: string;
  accept: string;
  disabled?: boolean;
  file: File | null;
  onChange: (file: File | null) => void;
};

function CustomizeFilePicker({
  id,
  label,
  accept,
  disabled,
  file,
  onChange,
}: CustomizeFilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="sme-report-customize__file">
      <span className="sme-report-customize__file-label">{label}</span>
      <div className="sme-report-customize__file-row">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          className="sme-report-customize__file-input"
          onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Choose file
        </Button>
        <span className="sme-report-customize__file-name" title={file?.name ?? undefined}>
          {file?.name ?? "No file chosen"}
        </span>
        {file ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="sme-report-customize__file-clear"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              if (inputRef.current) {
                inputRef.current.value = "";
              }
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function ReportCustomizeModal({
  open,
  onClose,
  projectId,
  clientUserId,
  report,
  onDraftSaved,
}: Props) {
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [workingHtml, setWorkingHtml] = useState("");
  const [payload, setPayload] = useState<unknown>(null);
  const [options, setOptions] = useState<ReportEditOptionsResponse | null>(null);
  const [dirty, setDirty] = useState(false);
  const [applying, setApplying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [regeneratedDraft, setRegeneratedDraft] = useState<RegeneratedDraftPack | null>(null);
  const [lastSkipped, setLastSkipped] = useState<string[]>([]);

  const [swapSelections, setSwapSelections] = useState<Record<string, string>>({});
  const [addChartPage, setAddChartPage] = useState("");
  const [addChartId, setAddChartId] = useState("");
  const [addPageLayout, setAddPageLayout] = useState("");
  const [addPageHeading, setAddPageHeading] = useState("");
  const [addPageSubtitle, setAddPageSubtitle] = useState("");
  const [addPageText, setAddPageText] = useState("");
  const [addPageChartId, setAddPageChartId] = useState("");
  const [addPageAfter, setAddPageAfter] = useState("");
  const [addPageAiWrite, setAddPageAiWrite] = useState(true);
  const [addPageImageCaption, setAddPageImageCaption] = useState("");
  const [addPageContentFile, setAddPageContentFile] = useState<File | null>(null);
  const [addPageImageFile, setAddPageImageFile] = useState<File | null>(null);
  const [removeCustomPage, setRemoveCustomPage] = useState("");

  const resetFormFields = useCallback(() => {
    setSwapSelections({});
    setAddChartPage("");
    setAddChartId("");
    setAddPageLayout("");
    setAddPageHeading("");
    setAddPageSubtitle("");
    setAddPageText("");
    setAddPageChartId("");
    setAddPageAfter("");
    setAddPageAiWrite(true);
    setAddPageImageCaption("");
    setAddPageContentFile(null);
    setAddPageImageFile(null);
    setRemoveCustomPage("");
    setLastSkipped([]);
  }, []);

  const loadSession = useCallback(async () => {
    if (!open || !projectId || !report.reportItemId) return;

    setLoadingSession(true);
    setSessionError("");
    resetFormFields();
    setDirty(false);
    setRegeneratedDraft(null);

    try {
      const [html, editPayload] = await Promise.all([
        fetchReportSourceHtml(projectId, report.reportItemId),
        fetchReportEditPayload(projectId),
      ]);

      setWorkingHtml(html);
      setPayload(editPayload);

      const opts = await fetchReportEditOptions({
        projectId,
        reportItemId: report.reportItemId,
        html,
        payload: editPayload,
      });
      setOptions(opts);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to open report customization.";
      setSessionError(message);
      setWorkingHtml("");
      setPayload(null);
      setOptions(null);
    } finally {
      setLoadingSession(false);
    }
  }, [open, projectId, report.reportItemId, resetFormFields]);

  useEffect(() => {
    if (open) {
      void loadSession();
    }
  }, [open, loadSession]);

  useEffect(() => {
    if (!open) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || applying || regenerating) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [open, dirty, applying, regenerating]);

  const chartOptions = useMemo(
    () => buildChartSelectOptions(options?.charts ?? [], false),
    [options?.charts]
  );

  const chartOptionsWithHeight = useMemo(
    () => buildChartSelectOptions(options?.charts ?? [], true),
    [options?.charts]
  );

  const pendingSwaps = useMemo(() => {
    return (options?.slots ?? []).filter((slot) => {
      const selected = swapSelections[slot.slot] ?? slot.chart_id;
      return selected !== slot.chart_id;
    });
  }, [options?.slots, swapSelections]);

  const renderChartPreview = (chartId: string, label?: string, fullWidth = false) => {
    if (payload == null || !workingHtml) return null;
    return (
      <ReportChartPreview
        chartId={chartId}
        payload={payload}
        reportHtml={workingHtml}
        disabled={applying || regenerating}
        chartLabel={label}
        fullWidth={fullWidth}
      />
    );
  };

  const chartById = useMemo(() => {
    const map = new Map<string, ReportEditChartOption>();
    for (const chart of options?.charts ?? []) {
      map.set(chart.chart_id, chart);
    }
    return map;
  }, [options?.charts]);

  const pagesWithRoom = useMemo(() => {
    if (!options?.pages?.length || !addChartId) {
      return options?.pages ?? [];
    }
    const chart = chartById.get(addChartId);
    const minSpace = (chart?.height ?? 0) + CHART_SPACE_PADDING;
    return (options.pages ?? []).filter((p) => p.free_space >= minSpace);
  }, [options?.pages, addChartId, chartById]);

  const customPages = useMemo(
    () => (options?.pages ?? []).filter((p) => p.custom),
    [options?.pages]
  );

  const selectedLayout = useMemo(
    () => (options?.layouts ?? []).find((l) => l.layout === addPageLayout),
    [options?.layouts, addPageLayout]
  );

  const refreshAfterEdit = async (nextHtml: string, skipped: string[]) => {
    setWorkingHtml(nextHtml);
    setDirty(true);
    setLastSkipped(skipped);
    const opts = await fetchReportEditOptions({
      projectId,
      reportItemId: report.reportItemId,
      html: nextHtml,
      payload: payload!,
    });
    setOptions(opts);
    resetFormFields();
  };

  const runOperations = async (
    operations: ReportEditOperation[],
    files?: File[]
  ) => {
    if (!payload || !workingHtml) {
      throw new Error("Customize session is not ready.");
    }

    setApplying(true);
    setSessionError("");
    try {
      const result = await applyReportEdit({
        projectId,
        reportItemId: report.reportItemId,
        html: workingHtml,
        payload,
        operations,
        files,
      });

      const skipped = (result.skipped ?? []).map((s) => s.reason).filter(Boolean);
      if (skipped.length) {
        await showAlert({
          type: "warning",
          title: "Some changes were skipped",
          message: skipped.join("\n"),
          confirmText: "OK",
        });
      }

      await refreshAfterEdit(result.html, skipped);
    } finally {
      setApplying(false);
    }
  };

  const handleApplySwaps = async () => {
    if (pendingSwaps.length === 0) {
      await showAlert({
        type: "info",
        title: "No swaps selected",
        message: "Choose a different chart in at least one row, then apply swaps.",
        confirmText: "OK",
      });
      return;
    }

    const operations: ReportEditOperation[] = pendingSwaps.map((slot) => ({
      action: "swap",
      slot: slot.slot,
      chart_id: swapSelections[slot.slot] ?? slot.chart_id,
    }));

    await runOperations(operations);
  };

  const handleRemoveChart = async (slot: ReportEditSlot) => {
    const ok = await confirmAlert({
      type: "warning",
      title: "Remove chart",
      message: `Remove the chart from ${slotRowLabel(slot)}? If it is the only chart on that page, the page will be removed too.`,
      confirmText: "Remove",
      cancelText: "Cancel",
    });
    if (!ok) return;
    await runOperations([{ action: "remove", slot: slot.slot }]);
  };

  const handleAddChart = async () => {
    const pageNum = parseInt(addChartPage, 10);
    if (!pageNum || !addChartId) {
      await showAlert({
        type: "warning",
        title: "Missing fields",
        message: "Choose a page and a chart to add.",
        confirmText: "OK",
      });
      return;
    }
    await runOperations([{ action: "add", page: pageNum, chart_id: addChartId }]);
  };

  const buildAddPageOperation = (): {
    operation: ReportEditOperation;
    files: File[];
  } | null => {
    const layout = addPageLayout.trim();
    if (!layout || !addPageHeading.trim()) {
      return null;
    }

    const op: ReportEditOperation = {
      action: "add_page",
      layout,
      heading: addPageHeading.trim(),
    };

    if (addPageSubtitle.trim()) {
      op.subtitle = addPageSubtitle.trim();
    }

    const after = parseInt(addPageAfter, 10);
    if (after > 0) {
      op.after_page = after;
    }

    const files: File[] = [];

    if (layoutNeedsChart(layout)) {
      if (!addPageChartId) return null;
      op.chart_id = addPageChartId;
    }

    if (layoutNeedsText(layout)) {
      const typed = addPageText.trim();
      if (typed) {
        op.content_text = typed.slice(0, TEXT_MAX);
      }
      if (addPageContentFile) {
        op.content_file = addPageContentFile.name;
        files.push(addPageContentFile);
      }
      if (!typed && !addPageContentFile) {
        return null;
      }
      op.ai_write = addPageAiWrite;
    }

    if (layoutNeedsImage(layout)) {
      if (!addPageImageFile) return null;
      op.image_file = addPageImageFile.name;
      files.push(addPageImageFile);
      if (addPageImageCaption.trim()) {
        op.image_caption = addPageImageCaption.trim();
      }
    }

    return { operation: op, files };
  };

  const handleAddPage = async () => {
    const built = buildAddPageOperation();
    if (!built) {
      await showAlert({
        type: "warning",
        title: "Missing fields",
        message:
          "Fill in the heading and all fields required for the selected layout (text, chart, and/or image).",
        confirmText: "OK",
      });
      return;
    }
    await runOperations([built.operation], built.files);
  };

  const handleRemovePage = async () => {
    const pageNum = parseInt(removeCustomPage, 10);
    if (!pageNum) {
      await showAlert({
        type: "warning",
        title: "Choose a page",
        message: "Select a custom page to remove.",
        confirmText: "OK",
      });
      return;
    }
    const ok = await confirmAlert({
      type: "warning",
      title: "Remove custom page",
      message: "This removes a page you added after generation. This cannot be undone without saving a new draft.",
      confirmText: "Remove page",
      cancelText: "Cancel",
    });
    if (!ok) return;
    await runOperations([{ action: "remove_page", page: pageNum }]);
  };

  const handleClose = async () => {
    if (applying || regenerating || savingDraft) return;
    if (regeneratedDraft) {
      const ok = await confirmAlert({
        type: "warning",
        title: "Discard customized PDF?",
        message:
          "You regenerated a PDF but have not saved it to a draft yet. Close without saving?",
        confirmText: "Discard",
        cancelText: "Stay",
      });
      if (!ok) return;
    } else if (dirty) {
      const ok = await confirmAlert({
        type: "warning",
        title: "Discard changes?",
        message:
          "You have edits that are not in a PDF yet. Regenerate PDF to preview them, or discard and close.",
        confirmText: "Discard",
        cancelText: "Stay",
      });
      if (!ok) return;
    }
    onClose();
  };

  const handleSaveRegeneratedDraft = async (replaceDraft: boolean) => {
    if (!regeneratedDraft) return;
    if (!clientUserId) {
      await showAlert({
        type: "warning",
        title: "Cannot save",
        message: "Client user is missing for this project.",
        confirmText: "OK",
      });
      return;
    }

    setSavingDraft(true);
    setSessionError("");
    try {
      const remarks = (report.remarks ?? "").trim();
      const result = await addOrUpdateReport(
        projectId,
        clientUserId,
        remarks,
        [regeneratedDraft.pdfFile],
        {
          status: "edit",
          reportItemId: replaceDraft ? report.reportItemId : null,
          replaceDraft,
          sourceHtmlPath: regeneratedDraft.sourceHtmlPath,
        }
      );

      const savedId = extractSavedReportItemId(result) ?? report.reportItemId;
      await showAlert({
        type: "success",
        title: "Draft saved",
        message: replaceDraft
          ? `Draft "${savedId}" was updated with your customizations. Submit it from the Saved list when ready.`
          : `New draft "${savedId}" was saved. Submit it from the Saved list when ready.`,
        confirmText: "Got it",
      });
      setRegeneratedDraft(null);
      onDraftSaved?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save the draft.";
      setSessionError(message);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleRegeneratePdf = async () => {
    if (!workingHtml || !dirty) {
      await showAlert({
        type: "info",
        title: "Nothing to regenerate",
        message: "Make at least one customization change, then regenerate the PDF.",
        confirmText: "OK",
      });
      return;
    }

    setRegenerating(true);
    setSessionError("");
    try {
      const sourceHtmlPath = await stageReportEditHtml(projectId, workingHtml);
      const pdfBlob = await renderReportEditPdf(workingHtml);
      const pdfFile = new File([pdfBlob], `${report.reportItemId}.pdf`, {
        type: "application/pdf",
      });

      setDirty(false);
      setRegeneratedDraft({ pdfFile, sourceHtmlPath });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to regenerate PDF.";
      setSessionError(message);
    } finally {
      setRegenerating(false);
    }
  };

  const renderSlotsTab = () => {
    const slots = options?.slots ?? [];
    if (slots.length === 0) {
      return (
        <p className="sme-report-customize__hint m-0">
          No chart slots were found in this report.
        </p>
      );
    }

    return (
      <div className="sme-report-customize__tab-panel">
        <p className="sme-report-customize__tab-lede m-0">
          Pick a new chart type for any slot, preview it, then apply all swaps in one step.
          Remove a chart if you want that slot cleared from the report.
        </p>
        <div className="sme-report-customize__stack">
          {slots.map((slot) => {
            const selectedChartId = swapSelections[slot.slot] ?? slot.chart_id;
            const selectedLabel = chartById.get(selectedChartId)?.label;
            const isPending =
              selectedChartId !== slot.chart_id;
            return (
              <article
                key={slot.slot}
                className={`sme-report-customize__slot-card${
                  isPending ? " sme-report-customize__slot-card--pending" : ""
                }`}
              >
                <header className="sme-report-customize__slot-head">
                  <div>
                    <p className="sme-report-customize__row-title m-0">{slotRowLabel(slot)}</p>
                    <p className="sme-report-customize__row-meta m-0">
                      Current <strong>{slot.chart_id}</strong>
                      {isPending ? (
                        <span className="sme-report-customize__pending-badge"> Swap pending</span>
                      ) : null}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={applying}
                    onClick={() => void handleRemoveChart(slot)}
                  >
                    Remove chart
                  </Button>
                </header>
                <Select
                  label="Replace with"
                  options={chartOptions}
                  value={selectedChartId}
                  onChange={(value) =>
                    setSwapSelections((prev) => ({ ...prev, [slot.slot]: value }))
                  }
                  disabled={applying}
                />
                {renderChartPreview(selectedChartId, selectedLabel, true)}
              </article>
            );
          })}
        </div>
        <div className="sme-report-customize__tab-actions">
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={applying || pendingSwaps.length === 0}
            isLoading={applying}
            onClick={() => void handleApplySwaps()}
          >
            {pendingSwaps.length > 0
              ? `Apply ${pendingSwaps.length} chart swap${pendingSwaps.length === 1 ? "" : "s"}`
              : "Apply chart swaps"}
          </Button>
        </div>
      </div>
    );
  };

  const renderAddChartTab = () => (
    <div className="sme-report-customize__tab-panel">
      <p className="sme-report-customize__tab-lede m-0">
        Choose a chart and target page. Heights in the list help you pick a chart that fits
        the free space on the page.
      </p>
      <div className="sme-report-customize__form">
      <Select
        label="Chart to add"
        options={chartOptionsWithHeight}
        value={addChartId}
        onChange={setAddChartId}
        placeholder="Select chart…"
        disabled={applying}
      />
      {addChartId
        ? renderChartPreview(addChartId, chartById.get(addChartId)?.label, true)
        : null}
      <Select
        label="Target page (must have enough space)"
        options={pagesWithRoom.map((p) => ({
          value: String(p.page),
          label: `${pageLabel(p)} · ${p.free_space}px free`,
        }))}
        value={addChartPage}
        onChange={setAddChartPage}
        placeholder="Select page…"
        disabled={applying || !addChartId}
      />
      <Button
        type="button"
        variant="primary"
        disabled={applying || !addChartPage || !addChartId}
        isLoading={applying}
        onClick={() => void handleAddChart()}
      >
        Add chart to existing page
      </Button>
      </div>
    </div>
  );

  const renderAddPageTab = () => (
    <div className="sme-report-customize__tab-panel">
      <p className="sme-report-customize__tab-lede m-0">
        Insert a new page using a layout template. Content can be typed, uploaded, or written
        by AI depending on the layout.
      </p>
      <div className="sme-report-customize__form">
      <Select
        label="Layout"
        options={(options?.layouts ?? []).map((l: ReportEditLayout) => ({
          value: l.layout,
          label: layoutDisplayLabel(l),
        }))}
        value={addPageLayout}
        onChange={setAddPageLayout}
        placeholder="Select layout…"
        disabled={applying}
      />
      <Input
        label="Heading"
        value={addPageHeading}
        onChange={(e) => setAddPageHeading(e.target.value)}
        disabled={applying}
        required
      />
      <Input
        label="Subtitle (optional)"
        value={addPageSubtitle}
        onChange={(e) => setAddPageSubtitle(e.target.value)}
        disabled={applying}
      />
      <Select
        label="Insert after page (optional — end if empty)"
        options={(options?.pages ?? []).map((p) => ({
          value: String(p.page),
          label: pageLabel(p),
        }))}
        value={addPageAfter}
        onChange={setAddPageAfter}
        placeholder="End of report"
        disabled={applying}
      />

      {selectedLayout && layoutNeedsChart(addPageLayout) ? (
        <>
          <Select
            label="Chart"
            options={chartOptionsWithHeight}
            value={addPageChartId}
            onChange={setAddPageChartId}
            placeholder="Select chart…"
            disabled={applying}
          />
          {addPageChartId
            ? renderChartPreview(addPageChartId, chartById.get(addPageChartId)?.label, true)
            : null}
        </>
      ) : null}

      {selectedLayout && layoutNeedsText(addPageLayout) ? (
        <section
          className="sme-report-customize__content-block"
          aria-labelledby="add-page-content-block-title"
        >
          <h4 id="add-page-content-block-title" className="sme-report-customize__content-block-title">
            Content
          </h4>
          <Switch
            label="AI content enhancement"
            checked={addPageAiWrite}
            onCheckedChange={setAddPageAiWrite}
            disabled={applying}
          />
          <Textarea
            label={`Body text (optional if you attach a file, max ${TEXT_MAX} chars)`}
            rows={5}
            value={addPageText}
            onChange={(e) => setAddPageText(e.target.value)}
            maxLength={TEXT_MAX}
            disabled={applying}
          />
          <CustomizeFilePicker
            id="add-page-content-file"
            label={`Content file (${ACCEPT_TEXT})`}
            accept={ACCEPT_TEXT}
            disabled={applying}
            file={addPageContentFile}
            onChange={setAddPageContentFile}
          />
        </section>
      ) : null}

      {selectedLayout && layoutNeedsImage(addPageLayout) ? (
        <>
          <CustomizeFilePicker
            id="add-page-image-file"
            label={`Image (${ACCEPT_IMAGE})`}
            accept={ACCEPT_IMAGE}
            disabled={applying}
            file={addPageImageFile}
            onChange={setAddPageImageFile}
          />
          <Input
            label="Image caption (optional)"
            value={addPageImageCaption}
            onChange={(e) => setAddPageImageCaption(e.target.value)}
            disabled={applying}
          />
        </>
      ) : null}

      <Button
        type="button"
        variant="primary"
        disabled={applying || !addPageLayout}
        isLoading={applying}
        onClick={() => void handleAddPage()}
      >
        Add a new page
      </Button>
      </div>
    </div>
  );

  const renderRemovePageTab = () => {
    if (customPages.length === 0) {
      return (
        <div className="sme-report-customize__tab-panel">
          <p className="sme-report-customize__hint m-0">
            There are no custom pages to remove. Only pages you added after generation can be
            removed.
          </p>
        </div>
      );
    }

    return (
      <div className="sme-report-customize__tab-panel">
        <p className="sme-report-customize__tab-lede m-0">
          Remove a page you added during customization. Built-in report pages cannot be deleted
          here.
        </p>
      <div className="sme-report-customize__form">
        <Select
          label="Custom page"
          options={customPages.map((p) => ({
            value: String(p.page),
            label: pageLabel(p),
          }))}
          value={removeCustomPage}
          onChange={setRemoveCustomPage}
          placeholder="Select page…"
          disabled={applying}
        />
        <Button
          type="button"
          variant="outline"
          disabled={applying || !removeCustomPage}
          onClick={() => void handleRemovePage()}
        >
          Remove custom page
        </Button>
      </div>
      </div>
    );
  };

  const renderSaveStep = () => (
    <div className="sme-report-customize__save-step" role="dialog" aria-labelledby="customize-save-step-title">
      <div className="sme-report-customize__save-step-card">
        <h3 id="customize-save-step-title" className="sme-report-customize__save-step-title">
          Save customized report
        </h3>
        <p className="sme-report-customize__save-step-lede m-0">
          Your PDF is ready. Store it as a draft, then submit to the client from the{" "}
          <strong>Saved</strong> list when you are ready.
        </p>
        <ul className="sme-report-customize__save-step-list">
          <li>
            <strong>Save as new draft</strong> — keeps <strong>{report.reportItemId}</strong> unchanged
            and creates another saved version.
          </li>
          <li>
            <strong>Replace {report.reportItemId}</strong> — overwrites that draft with this PDF.
          </li>
        </ul>
        {sessionError ? (
          <Alert variant="error" description={sessionError} className="sme-report-customize__alert" />
        ) : null}
        <div className="sme-report-customize__save-step-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleSaveRegeneratedDraft(false)}
            disabled={savingDraft}
            isLoading={savingDraft}
          >
            Save as new draft
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSaveRegeneratedDraft(true)}
            disabled={savingDraft}
            isLoading={savingDraft}
          >
            Replace {report.reportItemId}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={() => void handleClose()}
      title={regeneratedDraft ? "Save customized report" : "Customize report"}
      size="3xl"
      allowMaximize={!regeneratedDraft}
      footer={
        <div className="sme-report-customize__footer">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleClose()}
            disabled={regenerating || applying || savingDraft}
          >
            Close
          </Button>
          {!regeneratedDraft ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleRegeneratePdf()}
              disabled={!dirty || regenerating || applying || loadingSession}
              isLoading={regenerating}
            >
              Regenerate PDF
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="sme-report-customize" aria-busy={loadingSession || regenerating}>
        {loadingSession ? (
          <div className="sme-report-customize__loading-panel">
            <ContentLoader
              label="Loading report and edit options…"
              className="sme-report-customize__content-loader"
            />
            <p className="sme-report-customize__loading-hint m-0">
              Loading saved HTML, building the assessment payload, and fetching chart options.
            </p>
          </div>
        ) : regeneratedDraft ? (
          renderSaveStep()
        ) : (
          <>
            <p className="sme-report-customize__intro m-0">
              Draft <strong>{report.reportItemId}</strong>. Edits apply to a working HTML copy.
              Regenerate PDF when you are ready to preview and save.
            </p>

            {sessionError ? (
              <Alert
                variant="error"
                description={sessionError}
                className="sme-report-customize__alert"
              />
            ) : null}

            {!sessionError && options ? (
              <>
                {lastSkipped.length > 0 ? (
                  <Alert
                    variant="warning"
                    description={lastSkipped.join(" ")}
                    className="sme-report-customize__alert"
                  />
                ) : null}

                <Tabs
                  variant="segmented"
                  className="sme-report-customize__tabs"
                  defaultTab="slots"
                  tabs={[
                    {
                      id: "slots",
                      label: "Swap or remove charts",
                      content: renderSlotsTab(),
                    },
                    {
                      id: "add-chart",
                      label: "Add chart to existing page",
                      content: renderAddChartTab(),
                    },
                    { id: "add-page", label: "Add a new page", content: renderAddPageTab() },
                    {
                      id: "remove-page",
                      label: "Remove custom page",
                      content: renderRemovePageTab(),
                    },
                  ]}
                />
              </>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

export function canCustomizeSavedReport(report: {
  status?: string;
  sourceHtmlUrl?: string;
  SourceHtmlUrl?: string;
}): boolean {
  const status = String(report?.status || "").trim().toLowerCase();
  const htmlUrl = String(report?.sourceHtmlUrl || report?.SourceHtmlUrl || "").trim();
  return status === "edit" && htmlUrl.length > 0;
}

export function customizeBlockedMessage(report: { status?: string }): string | null {
  const status = String(report?.status || "").trim().toLowerCase();
  if (status === "upload") {
    return "Manually uploaded reports cannot be edited with AI. Generate and save an AI report first.";
  }
  if (status === "submitted") {
    return "Submitted reports cannot be customized. Save a draft from generate first.";
  }
  if (status === "edit") {
    return "This draft has no stored source HTML. Save an AI-generated report before customizing.";
  }
  return null;
}
